import { Dispatch, RefObject, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Grid2, Alert, CircularProgress, Fab, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import { FreightCard } from './FreightCard';
import { FilterBar } from './FilterBar';
// import { DateRangeDialog } from './DateRangeDialog';
import type { FreightDataChange, TimeFilterTabs } from '@/utilities/etransportTypes';
import { ETransportFreightPlutoDto, MinimalTarget, PlutoCustomerDto, PlutoFreightResponseDto } from '@/generated/model';
import { useTranslation } from 'react-i18next';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { ctxUser } from '@/contexts/UserContext';

import './etransport.css';

export function FreightCardGridView({
  targets,
  customer,
  freights,
  customers,
  isLoading,
  searchTarget,
  currentFreight,
  subscriptionButtonRef,
  showNoActiveSubscriptionBanner,
  onRefetch,
  setCustomer,
  setNewChanges,
  setSearchTarget,
  setCurrentFreight,
  setOpenFreightSubmitForm,
  setOpenSubscriptions,
  setOpenTargetFreightDetails,
  setOpenTargetFreightSubmitForm,
}: {
  targets: MinimalTarget[] | undefined;
  customer: PlutoCustomerDto | undefined;
  customers: PlutoCustomerDto[] | undefined;
  freights: PlutoFreightResponseDto | undefined;
  isLoading: boolean;
  searchTarget: string | undefined;
  currentFreight: ETransportFreightPlutoDto | undefined;
  subscriptionButtonRef: RefObject<HTMLButtonElement | null>;
  showNoActiveSubscriptionBanner: boolean;
  onRefetch: () => void;
  setCustomer: Dispatch<SetStateAction<PlutoCustomerDto | undefined>>;
  setNewChanges: Dispatch<SetStateAction<FreightDataChange | null>>;
  setSearchTarget: Dispatch<SetStateAction<string | undefined>>;
  setCurrentFreight: Dispatch<SetStateAction<ETransportFreightPlutoDto | undefined>>;
  setOpenFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
  setOpenSubscriptions: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightDetails: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const userCtx = useContext(ctxUser);
  const [startTime, setStartTime] = useState<number>(() => Date.now());
  const [activeTab, setActiveTab] = useState<TimeFilterTabs>('Active');
  const isAdmin = userCtx?.user.isSuperAdmin;

  /* AFTER MVP */
  // const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState<boolean>(false);
  // const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({
  //   start: '',
  //   end: '',
  // });
  /* AFTER MVP */

  useEffect(() => {
    const interval = setInterval(() => setStartTime(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  function handleOpenFreightSubmitForm(): void {
    setOpenSubscriptions(false);
    setOpenFreightSubmitForm(true);
    setOpenTargetFreightDetails(false);
    setOpenTargetFreightSubmitForm(false);
    setCurrentFreight(undefined);
  }

  function handleOpenSubscriptions(): void {
    setOpenFreightSubmitForm(false);
    setOpenTargetFreightDetails(false);
    setOpenTargetFreightSubmitForm(false);
    setCurrentFreight(undefined);
    setOpenSubscriptions(true);
  }

  const filteredFreights: ETransportFreightPlutoDto[] | undefined = useMemo(() => {
    if (freights === undefined) return undefined;
    const active: ETransportFreightPlutoDto[] = freights.active?.freights ?? [];
    const inactive: ETransportFreightPlutoDto[] = freights.inactive?.freights ?? [];
    const future: ETransportFreightPlutoDto[] = freights.future?.freights ?? [];

    switch (activeTab) {
      case 'Future':
        return future;
      case 'Inactive':
        return inactive;
      case 'Active':
        return active;
      case 'All':
        return [...future, ...active, ...inactive];
    }
  }, [freights, activeTab]);

  const categories: Map<TimeFilterTabs, number> = useMemo(() => {
    const active: number = freights?.active?.totalCount || 0;
    const inactive: number = freights?.inactive?.totalCount || 0;
    const future: number = freights?.future?.totalCount || 0;
    const counts = new Map<TimeFilterTabs, number>([
      ['All', active + inactive + future],
      ['Inactive', inactive],
      ['Active', active],
      ['Future', future],
    ]);

    return counts;
  }, [freights]);

  /* AFTER MVP */
  // const handleDateRangeApply = () => {
  //   if (customDateRange.start && customDateRange.end) {
  //     setTimeFilter('custom');
  //     setDateRangeDialogOpen(false);
  //   }
  // };

  // const handleClearDateRange = () => {
  //   setTimeFilter('all');
  //   setCustomDateRange({ start: '', end: '' });
  // };
  /* AFTER MVP */

  const freightsDidNotLoad: boolean = !isLoading && !freights && !targets;
  const freightsEmpty: boolean = !isLoading && !freightsDidNotLoad && !filteredFreights?.length;
  const freightsOk: boolean = !isLoading && !freightsEmpty;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: isPhoneOrHighScreen() ? '100%' : '100vh',
      }}
    >
      <div className={`topBar ${isPhoneOrHighScreen() ? 'phone' : ''}`}>
        <FilterBar
          isLoading={isLoading}
          customer={customer}
          customers={customers}
          activeTab={activeTab}
          categories={categories}
          searchTarget={searchTarget}
          subscriptionButtonRef={subscriptionButtonRef}
          setActiveTab={setActiveTab}
          setCustomer={setCustomer}
          onSearchChange={setSearchTarget}
          onOpenSubscriptions={handleOpenSubscriptions}
        />
      </div>

      <div
        className="panelOverlay"
        style={{
          paddingBottom: isPhoneOrHighScreen() ? 'var(--bottom-nav-bar-height)' : 75,
          paddingLeft: isPhoneOrHighScreen() ? '1%' : '0',
        }}
      >
        {showNoActiveSubscriptionBanner && (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={handleOpenSubscriptions}>
                {t('etransport.subscriptions.banner.action')}
              </Button>
            }
            sx={{ mb: 1 }}
          >
            {t('etransport.subscriptions.banner.message')}
          </Alert>
        )}

        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            flexDirection="column"
            gap={2}
          >
            <CircularProgress /> {t('etransport.cardview.loading')}
          </Box>
        )}

        {freightsDidNotLoad && (
          <>
            <Alert severity="error">{t('etransport.freightfrom.apierror')}</Alert>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              flexDirection="column"
              gap={2}
            >
              <img
                src="svg/NoSearchResult_ez_transparent.svg"
                alt="not_found"
                style={{ width: '100%', maxHeight: '250px' }}
                data-qa-element-id="no_result_image"
              />

              <Typography variant="h6" color="text.secondary" textAlign="center">
                {t('etransport.cardview.noload')}
              </Typography>
              <Button
                variant="outlined"
                onClick={onRefetch}
                sx={{
                  borderRadius: '25px',
                  boxShadow: 4,
                  backgroundColor: 'white',
                  color: 'var(--mui-palette-primary-main)',
                  border: '2px solid var(--mui-palette-primary-main)',
                  '&:hover': {
                    backgroundColor: 'rgba(21, 61, 146, 0.1)',
                  },
                }}
              >
                <ReplayIcon sx={{ mr: 1 }} />
                {t('etransport.cardview.retry')}
              </Button>
            </Box>
          </>
        )}

        {freightsEmpty && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            flexDirection="column"
            gap={2}
          >
            <img
              src="svg/NoSearchResult_ez_transparent.svg"
              alt="not_found"
              style={{ width: '100%', maxHeight: '250px' }}
              data-qa-element-id="no_result_image"
            />

            <Typography variant="h6" color="text.secondary" textAlign="center">
              {isAdmin && customer && '[' + customer.name + '] '}
              {t('etransport.cardview.nofreights')}
            </Typography>
            <Typography variant="h6" color="text.secondary" textAlign="center">
              {isAdmin && !customer && t('etransport.cardview.selectcustomer')}
            </Typography>
            {(searchTarget || activeTab !== 'All') && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTarget('');
                  setActiveTab('All');
                }}
                sx={{
                  borderRadius: '25px',
                  boxShadow: 4,
                  backgroundColor: 'white',
                  color: 'var(--mui-palette-primary-main)',
                  border: '2px solid var(--mui-palette-primary-main)',
                  '&:hover': {
                    backgroundColor: 'rgba(21, 61, 146, 0.1)',
                  },
                }}
              >
                <CloseIcon sx={{ mr: 1 }} /> {t('etransport.cardview.deletesearch')}
              </Button>
            )}
          </Box>
        )}

        {freightsOk && (
          <>
            <Grid2 container spacing={1}>
              {filteredFreights
                ?.slice()
                .sort((a, b) => (b.validityStartTimeMicros || 0) - (a.validityStartTimeMicros || 0))
                .map((freight, index) => (
                  <Grid2 container key={index}>
                    <FreightCard
                      active={freight.uit === currentFreight?.uit}
                      freight={freight}
                      targets={targets}
                      startTime={startTime}
                      setCurrentFreight={setCurrentFreight}
                      setOpenTargetFreightDetails={setOpenTargetFreightDetails}
                      setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
                      setOpenFreightSubmitForm={setOpenFreightSubmitForm}
                      setNewChanges={setNewChanges}
                    />
                  </Grid2>
                ))}
            </Grid2>

            {filteredFreights &&
              filteredFreights.length >= 100 &&
              (searchTarget === undefined || searchTarget === '') && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('etransport.cardview.maxresults')}
                </Alert>
              )}

            {/* AFTER MVP */}
            {/* <DateRangeDialog
              open={dateRangeDialogOpen}
              customDateRange={customDateRange}
              onClose={() => setDateRangeDialogOpen(false)}
              onDateRangeChange={setCustomDateRange}
              onApply={handleDateRangeApply}
            /> */}
            {/* AFTER MVP */}
          </>
        )}
      </div>

      <Stack
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: isPhoneOrHighScreen() ? 5 : 20,
          right: isPhoneOrHighScreen() ? 10 : 18,
        }}
      >
        <>
          <Fab
            variant="extended"
            onClick={handleOpenFreightSubmitForm}
            aria-label={t('etransport.cardview.add')}
            sx={{
              boxShadow: 6,
              backgroundColor: 'var(--mui-palette-secondary-main)',
              color: 'white',
              border: '2px solid white',
              '&:hover': {
                backgroundColor: 'var(--mui-palette-secondary-light)',
              },
            }}
          >
            <AddIcon sx={{ mr: 1 }} />
            {t('etransport.cardview.add')}
          </Fab>
        </>
      </Stack>
    </Box>
  );
}
